/**
 * Dotypos Busyness Analytics Types
 *
 * Type definitions for order data and busyness metrics from Dotypos POS
 */

// Raw order from Dotypos API
export interface DotyposOrderRaw {
  id: number;
  _cloudId: number;
  _branchId: number;
  _customerId?: number | null;
  _employeeId?: number | null;
  _tableId?: number | null;
  created: string; // ISO timestamp or milliseconds
  completed?: string | null;
  canceledDate?: string | null;
  locationDate?: string | null;
  status: DotyposOrderStatus;
  totalValueRounded: number;
  itemCount: number;
  guestCount?: number | null;
  currency: string;
  documentType: string;
  documentNumber?: string | null;
  paid: boolean;
  parked: boolean;
  tipAmount?: number | null;
  points?: number | null;
  flags?: number;
  versionDate?: string | null;
}

// Order status enum
export type DotyposOrderStatus =
  | 'NEW'
  | 'PARKED'
  | 'CLOSED'
  | 'CANCELED'
  | 'READY_TO_PICKUP'
  | 'READY_FOR_DELIVERY'
  | 'ON_DELIVERY'
  | 'DELIVERED'
  | 'DELIVERY_FAILED';

// Stored order in our database
export interface DotyposOrder {
  id: number;
  business_id: string;
  branch_id: number;
  created_at: string;
  completed_at: string | null;
  total_value: number;
  item_count: number;
  guest_count: number | null;
  status: string;
  payment_method_id: number | null;
  employee_id: number | null;
  customer_id: number | null;
  synced_at: string;
}

// Hourly busyness metrics
export interface BusynessHourlyMetric {
  id: string;
  business_id: string;
  branch_id: number;
  date: string;
  hour_of_day: number;
  day_of_week: number;
  order_count: number;
  total_revenue: number;
  avg_order_value: number | null;
  total_items: number;
  total_guests: number | null;
  created_at: string;
  updated_at: string;
}

// Sync log entry
export interface DotyposSyncLog {
  id: string;
  business_id: string;
  sync_type: 'orders' | 'sales_report' | 'branches';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at: string;
  completed_at: string | null;
  orders_synced: number;
  date_from: string | null;
  date_to: string | null;
  error_message: string | null;
  metadata: Record<string, unknown> | null;
}

// Aggregated busyness data for visualization
export interface BusynessStats {
  // Hourly patterns (average across all days)
  hourlyPattern: HourlyData[];

  // Day of week patterns
  dayOfWeekPattern: DayOfWeekData[];

  // Heatmap data (hour x day of week)
  heatmapData: HeatmapCell[];

  // Peak times
  peakHours: PeakTime[];
  peakDays: PeakDay[];

  // Summary stats
  totalOrders: number;
  totalRevenue: number;
  avgOrderValue: number;
  avgOrdersPerDay: number;
  busiestHour: number;
  busiestDay: number;

  // Date range
  dateFrom: string;
  dateTo: string;
}

// Hourly average data
export interface HourlyData {
  hour: number; // 0-23
  avgOrders: number;
  avgRevenue: number;
  totalOrders: number;
  totalRevenue: number;
}

// Day of week average data
export interface DayOfWeekData {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  dayName: string;
  avgOrders: number;
  avgRevenue: number;
  totalOrders: number;
  totalRevenue: number;
}

// Heatmap cell (hour x day of week)
export interface HeatmapCell {
  hour: number;
  dayOfWeek: number;
  dayName: string;
  avgOrders: number;
  avgRevenue: number;
  intensity: number; // 0-1 normalized value
}

// Peak time info
export interface PeakTime {
  hour: number;
  avgOrders: number;
  avgRevenue: number;
  rank: number;
}

// Peak day info
export interface PeakDay {
  dayOfWeek: number;
  dayName: string;
  avgOrders: number;
  avgRevenue: number;
  rank: number;
}

// Sync request parameters
export interface SyncOrdersRequest {
  business_id: string;
  date_from?: string; // ISO date
  date_to?: string; // ISO date
  force_full_sync?: boolean;
}

// Sync response
export interface SyncOrdersResponse {
  success: boolean;
  orders_synced: number;
  date_from: string;
  date_to: string;
  sync_log_id: string;
  error?: string;
}

// Branch info from Dotypos
export interface DotyposBranch {
  id: number;
  _cloudId: number;
  name: string;
  created: string;
  versionDate: string;
  deleted: boolean;
  display: boolean;
}

// Base Sales Report response (summarized)
export interface DotyposSalesReport {
  created: string;
  from: string;
  to: string;
  registerName: string;

  revenue: {
    totalWithVat: number;
    totalWithoutVat: number;
    vatBreakdown: Record<string, number>;
  };

  moneyTransactionInfo: {
    currency: string;
    salesCount: number;
    salesValue: number;
    cancelCount: number;
    cancelValue: number;
  };

  categorySales: Array<{
    categoryId: number;
    categoryName: string;
    revenue: number;
    quantity: number;
  }>;

  employeeSales: Array<{
    employeeId: number;
    employeeName: string;
    revenue: number;
    orderCount: number;
  }>;
}

// Day name helper
export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

// Short day names
export const DAY_NAMES_SHORT = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
] as const;

// Hour labels (12-hour format)
export const HOUR_LABELS_12H = [
  '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM',
  '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
  '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM',
  '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM',
] as const;

// Hour labels (24-hour format)
export const HOUR_LABELS_24H = [
  '00:00', '01:00', '02:00', '03:00', '04:00', '05:00',
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00', '21:00', '22:00', '23:00',
] as const;
